/**
 * The story link's feature (M7 plan S30): once the career (`m2`, with its write hook) and the strategic client
 * (`strategic`, order 170) are provided, creates the `story` service (`src/story-link/service.ts`, loaded lazily)
 * and binds the story Reach subsystem's ports (`src/story-link/subsystem.ts`, order 470). On a new career the story
 * re-apply list goes to the worker (the Charter career bridge, 171, shares the same `StorySync`).
 *
 * M7 wave 8: it also wires the UI (`src/story-link/ui.ts`): the comms panel's story port (`bindCommsStory`), every
 * story result's effects to the comms inbox (`postStoryEffects`), and the Logs screen's story link
 * (`logsScreen.connect`). The scan events carry the fitted scanner's Mk.
 *
 * M7 S37 part 2: it draws Missions 1 and 2's world content (`src/story-link/missions/scene.ts storyScene`: the Column
 * Nine field, its memorial or stripping, the barge wreck or rig) through the story props view (`props.ts`), rebuilt
 * only when the system, the story traces or the online day change (`watchChanged`, allocation-free per frame).
 *
 * M7 S37 part 3 (`src/story-link/wiring.ts`): player kills (named as story ships through the worker's story view),
 * recorded crimes and the courier leg's patrol scan (T6) reach the story service through the traffic and Charter ports.
 *
 * M7 S38 part 2: the worker facts (`view.ts`: `storyView()` and the relations feed, re-read on init, each online day
 * and each story result) give the comms stage variants (`stage`), the people logic's input (`input`) and the claim's
 * contact (`contactView`). A settled lease due time settles the story debts (`leaseDue`, `debts.ts`). Duarte's records
 * desk is a station tab at the Hub, and the Charter Board shows the Settlement 50-A line (`setRegistryNoteSource`).
 * M7 S39 part 3 (`finale.ts`): the finale flow ("Transit Priority") reaches the Logs screen as its `finale` source.
 * M7 S37d (`src/story-link/bargeWreck.ts`): the barge wreck's scene entry becomes a cuttable salvage wreck while the
 * session is ready, and its looted record is written once every part is taken (no farming).
 * M7 S40 part 2 (`src/story-link/reactivation/live.ts`): a targeted derelict survey shows its card in the Reach modal,
 * and the surveyed derelicts' repair-port prompts (vent, fence, hail, deliver, restart) reach the arbiter through the
 * story subsystem's `prompts` port; their result lines go to the comms surface.
 * M7 S40 part 3 (`src/story-link/reactivation/defence.ts`): the live restart defence (`beginRestart`) and the Wrecker
 * squat fight, stepped through the story subsystem's `tick` and `leave` ports.
 * M7 S43 (`src/story-link/reactivation/{register,look,harrow}.ts`): a lit restart's port offers the Registrant choice
 * (the Reach modal; registration filed on comms), the Harrow's port the Keep the Candle lit run, and the derelict looks
 * (lit windows, beacon, docking light; the Candle) are published to the structures feature on a Career or view change.
 */
import { FEATURE_ORDER, type GameContext, type GameFeature } from '../../app/feature'
import type { CareerService } from '../../career/flow'
import type { Career } from '../../career/types'
import type { ReactivationDebug } from '../../story-link/reactivation/debug'
import type { StoryService } from '../../story-link/service'
import type { M2Runtime } from '../../space/contracts'
import { bindCommsStory, postStoryEffects } from '../comms/port'
import logsScreen from '../logsScreen/feature'
import { setRegistryNoteSource } from '../../ui/charter/registryNote'
import { contactViewOf, createStoryViewCache, stageOf, worldInputOf } from './view'
import { createLiveFinale, epilogueFactsOf } from './finale'
import { createPropsWatch, createStoryPropsView, watchChanged, type StoryPropsView } from './props'
import type * as BargeWreck from '../../story-link/bargeWreck'

/** After the Charter career bridge (171) and the fleet link (172). */
const ORDER = FEATURE_ORDER.strategic + 3

let service: StoryService | null = null
const offs: (() => void)[] = []
/** Station registries the desk tab already went into (the registry has no removal). */
const deskRegistered = new WeakSet<object>()
/** The live desk check (the registered tab outlives one init; null while no story link runs). */
let deskAvailable: (() => boolean) | null = null
/** S37 part 2: the story props view and what rebuilds it. */
let props: StoryPropsView | null = null
let careerOf: (() => Career | null) | null = null
let rebuild: ((career: Career, systemId: string) => void) | null = null
const watch = createPropsWatch()
/** S43: publishes the derelict looks when the Career or the worker view changed (null outside the Reach). */
let publishLooks: (() => void) | null = null
/** S43: the reactivation dev hooks (`__debug.story.restart` / `register`; null outside the Reach). */
let reactivationDebug: ReactivationDebug | null = null
/** S37d: the barge wreck's per-frame step (null outside the Reach). */
let bargeStep: (() => void) | null = null

function hasWriteHook(m2: M2Runtime): m2 is CareerService {
  return 'applyDevCommand' in m2 && typeof m2.applyDevCommand === 'function'
}

/** The running story service (the debug API, S36), or null outside the Reach. */
export function storyLinkService(): StoryService | null {
  return service
}

/** S43: the reactivation dev hooks (the debug API), or null outside the Reach. */
export function storyLinkReactivationDebug(): ReactivationDebug | null {
  return reactivationDebug
}

const feature: GameFeature = {
  name: 'storyLink',
  order: ORDER,
  async init(ctx: GameContext): Promise<void> {
    const reach = ctx.reach
    const m2 = ctx.services.maybe('m2')
    const client = ctx.services.maybe('strategic')
    if (reach === null || m2 === undefined || client === undefined || !hasWriteHook(m2)) return
    const [{ createStoryService }, { careerAccess }, { bindStoryLinkPorts }, { stationDef, stationIndexOf }, { createStoryUi }, { storyData }, { storyDebtCycles }, m4, { createDuarteDeskPanel }] = await Promise.all([
      import('../../story-link/service'),
      import('../../bridge/m2/career'),
      import('../../story-link/subsystem'),
      import('../../data/m2'),
      import('../../story-link/ui'),
      import('../../story/data'),
      import('../../story-link/debts'),
      import('../../story-link/missions/m4'),
      import('../../story-link/desk'),
    ])
    const access = careerAccess(m2)
    const views = createStoryViewCache(client)
    const stage = (): ReturnType<typeof stageOf> => stageOf(views.view(), access.career())
    const systemOfPoi = (poiId: string): string | undefined => reach.galaxy.systems.find((s) => s.pois.some((p) => p.id === poiId))?.id
    const s = createStoryService({
      access: () => access,
      client,
      world: () => ({ systemId: reach.session.layout?.systemId, systemOfPoi }),
      reach: () => reach,
      writeSave: () => reach.persist('auto'),
      input: () => worldInputOf(views.view(), access.career()),
      // S39 part 5: the Wire epilogue reads the worker's gate stage, corporation and ban ballots as each item falls due.
      epilogueFacts: () => epilogueFactsOf(views.view()),
      // S39 part 6: the Blackmail stipend reads the worker's stability and gate stage each online day (none before the first read).
      stipendView: () => views.view(),
    })
    service = s
    ctx.services.provide('story', s)
    const dockedStation = (): string | null => {
      const r = m2.space.docking.readout
      return r.phase === 'docked' && r.station >= 0 ? (m2.space.stations.list[r.station]?.def.id ?? null) : null
    }
    // S40 part 2: the survey card and the surveyed derelicts' repair-port prompts.
    const [{ createReactivationLive }, { showModal }, { reactivationText }, { createReactivationDefence }, { createRegistrar }, looks, { defaultStorySites }, { showRegistrant }] = await Promise.all([
      import('../../story-link/reactivation/live'),
      import('../../reach/ui/modal'),
      import('../../story-link/reactivation/survey'),
      import('../../story-link/reactivation/defence'),
      import('../../story-link/reactivation/register'),
      import('../../story-link/reactivation/look'),
      import('../../story/reactivation/eligibility'),
      import('../../ui/story/Registrant'),
    ])
    let cardOpen = false
    let said = 0
    const say = (text: string): void => postStoryEffects([{ type: 'callback', callbackId: `reactivation.say:${++said}`, channel: 'comms', text }])
    const defence = createReactivationDefence({ service: s, career: () => access.career(), say })
    const registrar = createRegistrar({
      service: s,
      career: () => access.career(),
      contactView: () => contactViewOf(views.view(), client.relations),
      systemOfPoi,
      // S43: the Registrant screen (rows, terms, refusals; pick then confirm), not the Reach modal.
      choose: (_modal, model) => showRegistrant({ root: ctx.overlayRoot, model, ui: ctx.ui }),
      say,
      cfg: storyData().reactivation,
    })
    const { createReactivationDebug } = await import('../../story-link/reactivation/debug')
    reactivationDebug = createReactivationDebug({
      service: s,
      career: () => access.career(),
      galaxy: () => reach.galaxy,
      contactView: () => contactViewOf(views.view(), client.relations),
      systemOfPoi,
      say,
      cfg: storyData().reactivation,
    })
    const harrowPoiId = defaultStorySites(reach.galaxy).harrowPoiId
    let lookCareer: Career | null = null
    let lookView: ReturnType<typeof views.view> = null
    publishLooks = () => {
      const c = access.career()
      const v = views.view()
      if (c === lookCareer && v === lookView) return
      lookCareer = c
      lookView = v
      // The worker's `candleDark` (world flag `candle_dark`) darkens the Candle too.
      looks.derelictLookStore.set(c === null ? new Map() : looks.derelictLooks(c, harrowPoiId, looks.candleInputOf(v)))
    }
    const reactivation = createReactivationLive({
      service: s,
      career: () => access.career(),
      galaxy: () => reach.galaxy,
      showCard: (title, lines) => {
        if (cardOpen) return
        cardOpen = true
        const close = { id: 'close', label: reactivationText().card.close, primary: true }
        void showModal(ctx.overlayRoot, { title, body: lines.join('\n'), buttons: [close] }, ctx.ui).then(() => {
          cardOpen = false
        })
      },
      say,
      beginRestart: (site, core, sim) => defence.beginRestart(site, core, sim),
      squat: defence.squat,
      openRegistrant: (card) => void registrar.open(card),
      candle: true,
    })
    bindStoryLinkPorts({
      survey: (e) => reactivation.survey(e),
      prompts: reactivation.prompts,
      tick: (core, sim) => defence.step(core, sim),
      leave: (core, sim) => defence.leave(core, sim),
      service: s,
      career: () => access.career(),
      dockedStation,
      factionOfStation: (id) => (stationIndexOf(m2.data, id) >= 0 ? stationDef(m2.data, id).faction : null),
      scannerTier: () => reach.career.scannerMk(),
      meterInput: () => worldInputOf(views.view(), access.career()),
      leaseDue: (cycles) => {
        s.apply('story debt cycle', (c) => storyDebtCycles(c, cycles, storyData().leverage, m2.data))
      },
    })
    const ui = createStoryUi({
      service: s,
      career: () => access.career(),
      data: () => storyData(),
      reach: () => reach,
      ships: ctx.data.ships,
      stationName: (id) => (stationIndexOf(m2.data, id) >= 0 ? stationDef(m2.data, id).name : null),
      stage,
      contactView: () => contactViewOf(views.view(), client.relations),
      // The claim's faction day reads the live clock: the Career's gameTime is folded in only on a command or a save.
      gameTime: () => m2.space.mirror.gameTime,
    })
    const hub = m4.m4World().duarte.hubStation
    deskAvailable = () => {
      const c = access.career()
      return c !== null && dockedStation() === hub && m4.duarteDeskVisible(c)
    }
    if (!deskRegistered.has(reach.services)) {
      deskRegistered.add(reach.services)
      reach.services.register(createDuarteDeskPanel({ available: () => deskAvailable?.() ?? false }))
    }
    setRegistryNoteSource(() => {
      const c = access.career()
      return c === null ? null : m4.settlementRegistryLine(c)
    })
    void views.refresh()
    const [{ storyScene }, barge] = await Promise.all([import('../../story-link/missions/scene'), import('../../story-link/bargeWreck')])
    const view = createStoryPropsView(ctx.scene)
    props = view
    careerOf = () => access.career()
    const bargeState: BargeWreck.BargeWreckState = barge.createBargeWreckState()
    rebuild = (career, systemId) => {
      const sites = s.sites()
      const scene = sites === null ? [] : storyScene({ career, galaxy: reach.galaxy, sites, systemId, layoutOf: (id) => reach.layoutOf(id) })
      view.set(ctx, scene)
      barge.setBargeEntry(bargeState, barge.salvageEntry(scene), ctx.data.ships)
    }
    const recordLooted = (): void => void s.apply('story bargeLooted', (c) => ({ career: barge.recordBargeLooted(c), commands: [], effects: [] }))
    bargeStep = () => {
      const career = access.career()
      if (career !== null && reach.session.ready) barge.stepBargeWreck(bargeState, career, m2.space.salvage, recordLooted)
    }
    const [{ bindCharterPorts }, { setPlayerKillListener }, wiring] = await Promise.all([
      import('../../charter/subsystem'),
      import('../../traffic/sync'),
      import('../../story-link/wiring'),
    ])
    const systemNow = (): string => reach.session.layout?.systemId ?? ''
    bindCharterPorts({
      onCrime: (kind, _victim, systemId) => void s.report(wiring.crimeReport(kind, systemId)),
      storyContraband: () => wiring.contrabandAboard(access.career()),
      onStoryScan: (faction) => wiring.storyScan(s, access.career(), faction),
    })
    setPlayerKillListener((agentId, faction) => {
      void wiring.reportKill({ service: s, storyView: () => client.storyView() }, agentId, faction, systemNow())
    })
    offs.push(
      () => {
        bindCharterPorts({ onCrime: null, storyContraband: null, onStoryScan: null })
        setPlayerKillListener(null)
      },
      () => ui.dispose(),
      s.onEffects((effects) => postStoryEffects(effects)),
      s.onEffects(() => void views.refresh()),
      client.onOnlineDays(() => void views.refresh()),
      () => views.dispose(),
      () => setRegistryNoteSource(null),
      () => {
        deskAvailable = null
      },
      bindCommsStory(ui.comms),
      // M7 S39 part 3: the finale flow, opened from the Logs case tab's finale row.
      logsScreen.connect({
        ...ui.logs,
        finale: createLiveFinale({
          service: s,
          career: () => access.career(),
          node: (id) => ui.comms.view(id),
          contactView: () => contactViewOf(views.view(), client.relations),
          view: () => views.view(),
          systemId: () => reach.session.layout?.systemId ?? '',
          caseData: () => storyData(),
        }),
      }),
    )
  },
  frame(ctx: GameContext): void {
    const view = props
    const reach = ctx.reach
    if (view === null || reach === null || careerOf === null || rebuild === null) return
    const career = careerOf()
    const systemId = reach.session.layout?.systemId ?? null
    if (career !== null && systemId !== null && watchChanged(watch, systemId, career.story?.traces, career.story?.onlineDays ?? 0)) rebuild(career, systemId)
    view.frame(ctx.rig.pos)
    bargeStep?.()
    publishLooks?.()
  },
  dispose(): void {
    props?.dispose()
    props = null
    careerOf = null
    rebuild = null
    bargeStep = null
    publishLooks = null
    reactivationDebug = null
    watch.systemId = null
    watch.traces = null
    for (const off of offs.splice(0)) off()
    void import('../../story-link/subsystem').then(({ bindStoryLinkPorts }) => bindStoryLinkPorts(null))
    service?.dispose()
    service = null
  },
}

export default feature
